import {
  ChangeDetectionStrategy, Component, computed,
  contentChildren, effect, inject, input,
  InputSignal, model, ModelSignal, output,
  OutputEmitterRef,
  ViewEncapsulation,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Signal } from '@angular/core';


/**
 * utilities
*/
import { cascadeCheckboxSelection } from '../../utilities';

/**
 * interfaces
*/
import type { ITreeNode } from '../../interfaces';

/**
 * enums
 */
import { 
  ITreeSelectionMode, ITreeLoadingMode,
  TreeLoadingMode, TreeSelectionMode 
} from '../../enums';

/**
 * services
*/
import { TreeStateService } from '../../services';

/**
 * directives and components
*/
import { TreeNodeComponent } from '../tree-node/tree-node.component';
import { BlockUiDirective } from '@shared/directives/block-ui/block-ui.directive';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { FieldGroupComponent } from '@forms/components/field-group/field-group.component';
import { ListGroupDirective } from "@shared/directives/list-groupe/list-group.directive";
import { FormInputFieldDirective } from "@forms/directives/form-input-field/form-input-field.directive";
import { ElementTemplateDirective } from '@shared/directives/element-template/element-template.directive';

/**
 * @name TreeComponent
 * @description
 * The core host component for the tree system. It manages global state, 
 * inputs, and coordination between nested tree nodes.
 */
@Component({
  exportAs: 'treeApi',
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrl: './tree.component.scss',
  host: {
    '[class]': "'app_tree'"
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TreeStateService],
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    /**
     * directives
     */
    BlockUiDirective,
    ListGroupDirective,
    FormInputFieldDirective,
    ElementTemplateDirective,
    /**
     * components
     */
    TreeNodeComponent,
    FieldGroupComponent,
  ],
})
export class TreeComponent {

  /**
   * @description Direct access to the shared state management service.
   */
  public readonly treeState = inject(TreeStateService);

  /**
   * @description Projected `ng-template` slots registered with `appTemplate`.
   */
  public readonly templateDirectives = contentChildren(TemplateDirective);

  /**
   * @description Hierarchical data displayed by the tree.
   */
  public readonly value: InputSignal<ITreeNode<unknown>[]> = input<ITreeNode<unknown>[]>([]);

  /**
   * @description Defines how nodes are selected (single, multiple, or checkbox).
   * @default null
   */
  public readonly selectionMode: InputSignal<ITreeSelectionMode | null> = input<ITreeSelectionMode | null>(null);

  /**
   * @description Computed signal that determines if the current mode is 'checkbox'.
   */
  public readonly selectionModeCheckBox: Signal<boolean> = computed(() => this.selectionMode() === TreeSelectionMode.Checkbox)

  /**
   * @description Determines if the loading state appears as an inline icon or a full-mask overlay.
   * @default TreeLoadingMode.Icon
   */
  public readonly loadingMode: InputSignal<ITreeLoadingMode> = input<ITreeLoadingMode>(TreeLoadingMode.Icon);

  /**
   * @description Computed signal checking if the mask overlay should be displayed.
   */
  public readonly loadingIsMask: Signal<boolean> = computed(() => this.loadingMode() === TreeLoadingMode.Mask);

  /**
   * @description Two-way bound selection (single node, multiple nodes, or none depending on mode).
   * Also acts as the single source of truth for the form-control value when the tree is bound
   * via `[formControl]` / `formControlName`.
   */
  public readonly selection: ModelSignal<ITreeNode | ITreeNode[] | null> = model<ITreeNode | ITreeNode[] | null>(null);

  /**
   * @description When true, multiple selection uses meta keys (Ctrl/Cmd) without clearing prior picks.
   * @default false
   */
  public readonly metaKeySelection = input<boolean>(false);

  /**
   * @description Global busy flag for the entire tree (e.g. initial fetch).
   * @default false
   */
  public readonly loading = input<boolean>(false);

  /**
   * @description Icon class or token for inline loading (`loadingMode === 'icon'`).
   */
  public readonly loadingIcon = input<string>('fa-regular fa-circle-notch fa-spin');

  /**
   * @description If true, prevents the internal loading container from wrapping the content.
   * @default true
   */
  public readonly skipSelfLoadingContainer: InputSignal<boolean> = input<boolean>(true)

  /**
   * @description Message shown when there is no data to display.
   */
  public readonly emptyMessage: InputSignal<string> = input<string>('No data to display.');

  /**
   * @description Accessible name for the tree root element.
   */
  public readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * @description Default accessible label for expand/collapse toggles.
   */
  public readonly togglerAriaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * @description Element id that labels the tree root (`aria-labelledby`).
   */
  public readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * @description When true, shows the filter UI (implemented in Step 6).
   * @default false
   */
  public readonly filter: InputSignal<boolean> = input<boolean>(false);

  /**
   * @input filterBy
   * @description The property name or path (e.g., 'label' or 'data.price') used for searching.
   */
  public readonly filterBy: InputSignal<string> = input<string>('label');

  /**
   * @description Placeholder for the filter text box.
   */
  public readonly filterPlaceholder: InputSignal<string> = input<string>('');

  /**
   * @description Displays a loading spinner specifically inside the filter input.
   * @default false
   */
  public readonly filterLoading: InputSignal<boolean> = input<boolean>(false)

  /**
   * @description CSS height for the scrollable tree body (e.g. `400px`).
   */
  public readonly scrollHeight: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * @description Optional `trackBy` for rendered rows (Step 3+).
   */
  public readonly trackBy: InputSignal<(index: number, node: ITreeNode) => unknown> = input<((index: number, node: ITreeNode) => unknown)>((i, n) => n.key);

  /**
   * @description When true, selected rows receive a highlight class.
   * @default true
   */
  public readonly highlightOnSelect: InputSignal<boolean> = input<boolean>(true);

  /**
   * @event onNodeSelect
   * @description Emitted when a node becomes selected.
   */
  public readonly onNodeSelect: OutputEmitterRef<ITreeNode> = output<ITreeNode>();

  /**
   * @event onNodeUnselect
   * @description Emitted when a node is removed from the selection.
   */
  public readonly onNodeUnselect: OutputEmitterRef<ITreeNode> = output<ITreeNode>();

  /**
   * @event onNodeExpand
   * @description Emitted after a node is expanded.
   */
  public readonly onNodeExpand: OutputEmitterRef<ITreeNode> = output<ITreeNode>();

  /**
   * @event onNodeCollapse
   * @description Emitted after a node is collapsed.
   */
  public readonly onNodeCollapse: OutputEmitterRef<ITreeNode> = output<ITreeNode>();

  /**
   * @event onNodeDoubleClick
   * @description Emitted on double activation of a node.
   */
  public readonly onNodeDoubleClick: OutputEmitterRef<ITreeNode> = output<ITreeNode>();

  /**
   * @event onLazyLoad
   * @description Emitted when lazy children must be loaded for a node.
   */
  public readonly onLazyLoad: OutputEmitterRef<{node: ITreeNode}> = output<{ node: ITreeNode }>();

  /**
   * @event onFilter
   * @description Emitted when the filter input changes.
   */
  public readonly onFilter: OutputEmitterRef<{ filter: string, nodes: ITreeNode[] }> = output<{ filter: string, nodes: ITreeNode[] }>();

  /**
   * @event onScroll
   * @description Emitted when the scrollable tree body scrolls.
   */
  public readonly onScroll: OutputEmitterRef<Event> = output<Event>();

  /**
   * @description Read-only list of nodes after filtering (mirrors `TreeStateService.filteredNodes`).
   */
  public readonly filteredNodes: Signal<ITreeNode[]> = computed(() => this.treeState.filteredNodes());

  /**
   *
   */
  constructor() {
    
    //
    effect(() => {
      const _value = this.value()
      this.treeState.setValue(_value);
    })

    //
    effect(() => {
      
      const _filterBy = this.filterBy()

      this.treeState.setFilterBy(_filterBy)
    })
  }

  /**
   * @description Finds a named template slot among projected `TemplateDirective` instances.
   * @param tempName - Slot name (e.g. `header`, `node`, `empty`).
   * @returns The directive instance or `undefined`.
   */
  public getTemplate(tempName: string): TemplateDirective | undefined {
    const list = this.templateDirectives();
    return list.find((d) => d.name === tempName);
  }

  /**
   * @description Clears the active filter and restores full node visibility.
   * @returns void
   */
  public resetFilter(): void {
    this.treeState.resetFilter();
  }

  /**
   * @description Internal handler for filter changes; emits the onFilter output.
   * @param filter The search query string.
   */
  public handleFilter(filter: string): void {
    this.onFilter.emit({ filter, nodes: this.filteredNodes()})
  }

  /**
   * @description Expands all expandable nodes.
   * @returns void
   */
  public expandAll(): void {
    this.treeState.expandAll();
  }

  /**
   * @description Collapses all nodes.
   * @returns void
   */
  public collapseAll(): void {
    this.treeState.collapseAll();
  }

  /**
   * @description Handles node click events, updates selection model, and emits events.
   * @param node The clicked tree node
   * @param event The DOM Mouse event
   */
  public handleNodeClick(node: ITreeNode, event: MouseEvent): void {

    // no selection mode -> no-op
    const mode = this.selectionMode();
    if (mode == null) return;

    let newSelection: ITreeNode | ITreeNode[] | null;
    let isSelectedNow: boolean;

    if (mode === TreeSelectionMode.Checkbox) {
      // checkbox mode -> pure cascade helper
      const current = (Array.isArray(this.selection()) ? this.selection() : []) as ITreeNode[];
      const next = cascadeCheckboxSelection(node, current, this.treeState.value());
      newSelection = next;
      isSelectedNow = !!next?.some(n => n.key === node.key);
    } else {
      // single / multiple mode -> existing service logic
      const isMetaPressed = event.ctrlKey || event.metaKey;
      const result = this.treeState.toggleSelection(
        node, this.selection(), mode, this.metaKeySelection(), isMetaPressed,
      );
      newSelection = result.newSelection;
      isSelectedNow = result.isSelectedNow;
    }

    // single signal write -> all derived state (isSelected, isIndeterminate, etc.) updates
    this.selection.set(newSelection);

    // standard outputs
    if (isSelectedNow) this.onNodeSelect.emit(node);
    else this.onNodeUnselect.emit(node);
  }

  /**
   * @output onNodeDoubleClick
   * @description Emitted when a node is double-clicked.
   */
  public handleScroll(event: Event): void {
    this.onScroll.emit(event)
  }

}
